import { useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '../dialog';
import Loading from '../loading';
import LegalDocumentViewer, { LEGAL_DOCUMENT_DIALOG_PAPER_CLASS } from '../legal-document-viewer';
import { getTask, postTask } from '../../../services';
import { Urls } from '../../../config/urls';

interface LegalDocumentAcceptanceStatusItem {
  versionId: number;
  documentType: 'TERMS_AND_CONDITIONS' | 'PRIVACY_POLICY';
  versionCode: string;
  country?: string | null;
  language: string;
  contentUrl: string;
  acceptedCurrentVersion: boolean;
}

interface LegalDocumentAcceptanceStatusResponse {
  pendingAcceptance: boolean;
  documents: LegalDocumentAcceptanceStatusItem[];
}

interface LegalAcceptanceModalProps {
  token?: string;
  language?: string;
}

const COPY = {
  es: {
    title: 'Actualizamos nuestros documentos legales',
    body: 'Para continuar, revisa y acepta la version vigente de términos y condiciones y política de privacidad.',
    checkbox: 'He leído y acepto la version vigente de los documentos legales.',
    action: 'Aceptar y continuar',
    tabs: {
      TERMS_AND_CONDITIONS: 'Términos y condiciones',
      PRIVACY_POLICY: 'Política de privacidad',
    },
  },
  en: {
    title: 'We updated our legal documents',
    body: 'To continue, review and accept the current version of the terms and conditions and privacy policy.',
    checkbox: 'I have read and accept the current version of the legal documents.',
    action: 'Accept and continue',
    tabs: {
      TERMS_AND_CONDITIONS: 'Terms and conditions',
      PRIVACY_POLICY: 'Privacy policy',
    },
  },
  pt: {
    title: 'Atualizamos nossos documentos legais',
    body: 'Para continuar, revise e aceite a versao atual dos termos e condicoes e da politica de privacidade.',
    checkbox: 'Li e aceito a versao atual dos documentos legais.',
    action: 'Aceitar e continuar',
    tabs: {
      TERMS_AND_CONDITIONS: 'Termos e condicoes',
      PRIVACY_POLICY: 'Politica de privacidade',
    },
  },
};

const LegalAcceptanceModal = ({ token, language = 'en' }: LegalAcceptanceModalProps) => {
  const normalizedLanguage = language.toLowerCase().startsWith('pt')
    ? 'pt'
    : language.toLowerCase().startsWith('es')
      ? 'es'
      : 'en';
  const copy = COPY[normalizedLanguage];
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [status, setStatus] = useState<LegalDocumentAcceptanceStatusResponse | null>(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState<'TERMS_AND_CONDITIONS' | 'PRIVACY_POLICY'>(
    'TERMS_AND_CONDITIONS'
  );

  useEffect(() => {
    if (!token) {
      return;
    }

    let cancelled = false;

    const fetchStatus = async () => {
      setLoading(true);
      const { response } = await getTask({
        url: Urls.api.legalDocumentAcceptanceStatus,
        token,
      });

      if (!cancelled) {
        setStatus(response || { pendingAcceptance: false, documents: [] });
        const firstPending = response?.documents?.find(
          (item: LegalDocumentAcceptanceStatusItem) => !item.acceptedCurrentVersion
        );
        setSelectedDocumentType(firstPending?.documentType || 'TERMS_AND_CONDITIONS');
        setLoading(false);
      }
    };

    fetchStatus();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const documents = status?.documents || [];
  const pendingAcceptance = Boolean(status?.pendingAcceptance && documents.length > 0);
  const selectedDocument = useMemo(
    () => documents.find(document => document.documentType === selectedDocumentType) || documents[0],
    [documents, selectedDocumentType]
  );

  const handleAccept = async () => {
    if (!token || !status?.documents?.length) {
      return;
    }

    setSubmitting(true);
    const { response } = await postTask({
      url: Urls.api.acceptLegalDocuments,
      token,
      body: {
        versionIds: status.documents.map(document => document.versionId),
        language: normalizedLanguage,
      },
    });

    if (response) {
      setStatus({
        pendingAcceptance: false,
        documents: status.documents.map(document => ({ ...document, acceptedCurrentVersion: true })),
      });
      setAccepted(false);
    }
    setSubmitting(false);
  };

  if (loading || !pendingAcceptance) {
    return null;
  }

  return (
    <Dialog
      open
      type="md"
      dividers={false}
      unpaddingContent
      fullScreenMobile
      title={copy.title}
      paperClassName={LEGAL_DOCUMENT_DIALOG_PAPER_CLASS}
      body={
        <div style={{ padding: 16 }}>
          <p>{copy.body}</p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {documents.map(document => (
              <Button
                key={document.documentType}
                variant={document.documentType === selectedDocumentType ? 'contained' : 'outlined'}
                onClick={() => setSelectedDocumentType(document.documentType)}
              >
                {copy.tabs[document.documentType]}
              </Button>
            ))}
          </div>
          {selectedDocument ? (
            <LegalDocumentViewer
              documentType={selectedDocument.documentType}
              language={selectedDocument.language}
              contentUrl={selectedDocument.contentUrl}
            />
          ) : (
            <Loading />
          )}
          <div style={{ marginTop: 16 }}>
            <FormControlLabel
              control={<Checkbox checked={accepted} onChange={event => setAccepted(event.target.checked)} />}
              label={copy.checkbox}
            />
          </div>
        </div>
      }
      footer={
        <Button variant="contained" onClick={handleAccept} disabled={!accepted || submitting}>
          {copy.action}
        </Button>
      }
    />
  );
};

export default LegalAcceptanceModal;
